<template>
    <FilterComponent
        v-model="filterCopy"
        title="Filter Requests"
        :filters="filterConfig"
        :disabled="disabled"
        :is-loading="isLoadingOptions"
        :extra-active-filters="extraActiveFilters"
        @apply="handleApply"
        @reset="$emit('reset')"
        @remove-extra="handleRemoveExtra"
    >
        <template #additional-filters="{ model, update, apply }">
            <div class="gap-sm flex flex-col">
                <div class="gap-xs flex flex-col">
                    <label class="text-sm font-medium">Reference search</label>
                    <div class="input-wrapper pr-2.5">
                        <input
                            type="search"
                            :value="model.referenceSearch"
                            placeholder="Search reference ID..."
                            autocomplete="off"
                            class="w-full"
                            @input="update('referenceSearch', ($event.target as HTMLInputElement).value)"
                            @keydown.enter.prevent="apply()"
                        />
                        <i class="i-search" />
                    </div>
                </div>
                <div class="gap-xs flex flex-col">
                    <label class="text-sm font-medium">Summary search</label>
                    <div class="input-wrapper pr-2.5">
                        <input
                            type="search"
                            :value="model.summarySearch"
                            placeholder="Search summary..."
                            autocomplete="off"
                            class="w-full"
                            @input="update('summarySearch', ($event.target as HTMLInputElement).value)"
                            @keydown.enter.prevent="apply()"
                        />
                        <i class="i-search" />
                    </div>
                </div>
                <Button
                    v-if="hasLegacyOption"
                    variant="tertiary"
                    class="button mt-sm w-full justify-center"
                    @click="toggleLegacyInternal(model, update, apply)"
                >
                    {{ isLegacyActive(model) ? 'Show current tickets' : 'Show legacy tickets' }}
                </Button>
            </div>
        </template>

        <template #mobile-header>
            <div v-if="isLegacyFilterActive" class="mb-md border-l-4 border-(--state-info-border) bg-(--state-info-bg) p-4">
                <p class="text-(--state-info-fg)">Legacy request filter active</p>
            </div>
        </template>

        <template #mobile-footer-left="{ model, update, apply }">
            <Button
                v-if="hasLegacyOption"
                variant="tertiary"
                class="whitespace-nowrap"
                @click="toggleLegacyInternal(model, update, apply)"
            >
                {{ isLegacyActive(model) ? 'Show current tickets' : 'Show legacy tickets' }}
            </Button>
        </template>
    </FilterComponent>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import FilterComponent, { type ExtraActiveFilter, type FilterConfig } from '@/components/ui/FilterComponent.vue';
import type { Option, RequestFilter } from '@/stores/store-interfaces';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useSupportStore } from '@/stores/support';
import { useOrganizationStore } from '@/stores/organization';

const supportStore = useSupportStore();
const organizationStore = useOrganizationStore();
const isLoadingOptions = ref(false);
const legacyServiceDeskIds = [11, 39];
const defaultServiceDeskId = 74;

const props = defineProps<{
    selectedFilter: RequestFilter;
    disabled: boolean;
}>();

const emit = defineEmits<{
    (e: 'apply', value: RequestFilter): void;
    (e: 'reset'): void;
}>();

const filterCopy = ref<RequestFilter>({
    serviceDesks: [] as Option[],
    requestTypes: [] as Option[],
    statuses: [] as Option[],
    otherFilters: [] as Option[],
    referenceSearch: '',
    summarySearch: '',
});

const filterOptions = reactive<{
    serviceDeskOptions: Option[];
    requestTypeOptions: Option[];
    requestTypeStatusOptions: Option[];
    adminFilterOptions: Option[];
}>({
    serviceDeskOptions: [],
    requestTypeOptions: [],
    requestTypeStatusOptions: [],
    adminFilterOptions: [
        { value: 'Created by me', label: 'Created by me' },
        { value: 'I am a participant', label: 'I am a participant' },
    ],
});

const filterConfig = computed<FilterConfig[]>(() => [
    {
        key: 'requestTypes',
        label: 'Request types',
        options: filterOptions.requestTypeOptions,
        placeholder: 'Select request type',
        hidden: filterOptions.requestTypeOptions.length === 0,
    },
    {
        key: 'statuses',
        label: 'Request type status',
        options: filterOptions.requestTypeStatusOptions,
        placeholder: 'Select request type status',
        hidden: filterOptions.requestTypeStatusOptions.length === 0,
    },
    {
        key: 'otherFilters',
        label: 'Other filters',
        options: filterOptions.adminFilterOptions,
        placeholder: 'Select a filter',
        hidden: filterOptions.adminFilterOptions.length === 0,
    },
]);

const hasLegacyOption = computed(() =>
    filterOptions.serviceDeskOptions.some((option) => legacyServiceDeskIds.includes(option.value)),
);

const isLegacyFilterActive = computed(() => {
    return filterCopy.value.serviceDesks.some((sd) => legacyServiceDeskIds.includes(sd.value));
});

const extraActiveFilters = computed(() => {
    const filters: ExtraActiveFilter[] = [];
    const sd = filterCopy.value.serviceDesks[0];
    if (sd && sd.value.toString() !== '74') {
        filters.push({ label: 'Legacy', value: sd.value, categoryName: 'service desk' });
    }
    if (filterCopy.value.referenceSearch) {
        filters.push({
            label: filterCopy.value.referenceSearch,
            value: filterCopy.value.referenceSearch,
            categoryName: 'Reference',
        });
    }
    if (filterCopy.value.summarySearch) {
        filters.push({
            label: filterCopy.value.summarySearch,
            value: filterCopy.value.summarySearch,
            categoryName: 'Summary',
        });
    }
    return filters;
});

const setupAdminOptions = () => {
    if (
        organizationStore.user?.organizations
            .find((org) => org.organization_id === organizationStore.usersOrganization?.organization_id)
            ?.organization_roles.some((role) => role.name === 'org-admin' || role.name === 'org-ticket-viewer')
    ) {
        if (!filterOptions.adminFilterOptions.some((opt) => opt.value === 'Show all')) {
            filterOptions.adminFilterOptions.unshift({ value: 'Show all', label: 'Show all' });
        }
    }
};

onMounted(async () => {
    setupAdminOptions();

    const serviceDesks = await supportStore.getServiceDesks();
    if (serviceDesks === undefined) return;
    serviceDesks.forEach((serviceDesk) => {
        filterOptions.serviceDeskOptions.push({
            value: serviceDesk.service_desk_id,
            label: serviceDesk.name,
        });
    });
    // Initialize from props
    filterCopy.value = JSON.parse(JSON.stringify(props.selectedFilter));

    if (filterCopy.value.serviceDesks.length === 0) {
        const defaultServiceDesk = filterOptions.serviceDeskOptions.find((sd) => sd.value === 74);
        filterCopy.value.serviceDesks.push(defaultServiceDesk ?? filterOptions.serviceDeskOptions[0]);
    }
    await setupOptions(filterCopy.value.serviceDesks);
});

watch(
    () => organizationStore.user,
    () => setupAdminOptions(),
);

watch(
    () => organizationStore.usersOrganization,
    () => setupAdminOptions(),
);

// Watch for service desk changes to clear other filters and fetch options
watch(
    () => filterCopy.value.serviceDesks,
    async (newVal, oldVal) => {
        // Only react if value actually changed (deep check might be needed for arrays but here reference changes or length changes)
        // But with v-model replacement, reference changes.
        // We verify if IDs are different to avoid unnecessary clears if it's just a re-assignment of same
        const newId = newVal?.[0]?.value;
        const oldId = oldVal?.[0]?.value;

        if (newId !== oldId) {
            // Service desk changed, clear others
            // EXCEPT during initial load/sync from props?
            // The watch logic in original file was manual call to handleFilterUpdate.
            // But here, if user changes legacy/ticket, we want to clear.

            // Wait, if props change (external update), we update filterCopy. That triggers this watch.
            // We should NOT clear filters if it comes from props update.
            // But props update sets filterCopy.

            // Actually, existing logic:
            // handleFilterUpdate (user action) -> clears others.
            // watch(props.selectedFilter) -> updates filterCopy -> calls setupOptions.

            // So if I modify filterCopy.value.serviceDesks manually (e.g. toggle button), I should clear others.
            // If it comes from props, maybe I shouldn't?

            // Let's rely on explicit clearing in the toggle functions, and use watch ONLY for setupOptions.
            await setupOptions(newVal);
        }
    },
);

function isLegacyActive(model: RequestFilter) {
    return model.serviceDesks?.some((sd: Option) => legacyServiceDeskIds.includes(sd.value));
}

function toggleLegacyInternal(model: RequestFilter, update: (key: string, val: unknown) => void, apply: () => void) {
    const active = isLegacyActive(model);
    let newDesks: Option[];

    if (active) {
        // Switch to default
        const defaultSD = filterOptions.serviceDeskOptions.find((sd) => sd.value == defaultServiceDeskId);
        newDesks = defaultSD ? [defaultSD] : [];
    } else {
        // Switch to legacy
        const legacySD = filterOptions.serviceDeskOptions.find((sd) => legacyServiceDeskIds.includes(sd.value));
        newDesks = legacySD ? [legacySD] : [];
    }

    // Update service desks
    update('serviceDesks', newDesks);

    // Clear dependent filters
    update('requestTypes', []);
    update('statuses', []);
    update('otherFilters', []);

    // Instantly apply the filter
    apply();
}

function handleRemoveExtra(filter: ExtraActiveFilter) {
    // If it's legacy (or any extra filter mapped to service desk really)
    // In extraActiveFilters we set label 'Legacy'
    if (filter.label === 'Legacy') {
        const defaultSD = filterOptions.serviceDeskOptions.find((sd) => sd.value == defaultServiceDeskId);
        filterCopy.value.serviceDesks = defaultSD ? [defaultSD] : [];

        // Clear dependent filters
        filterCopy.value.requestTypes = [];
        filterCopy.value.statuses = [];
        filterCopy.value.otherFilters = [];

        // Emit apply
        emit('apply', JSON.parse(JSON.stringify(filterCopy.value)));
        return;
    }

    if (filter.categoryName === 'Reference') {
        filterCopy.value.referenceSearch = '';
        emit('apply', JSON.parse(JSON.stringify(filterCopy.value)));
        return;
    }

    if (filter.categoryName === 'Summary') {
        filterCopy.value.summarySearch = '';
        emit('apply', JSON.parse(JSON.stringify(filterCopy.value)));
    }
}

function handleApply(value: RequestFilter) {
    emit('apply', value);
}

const setupOptions = async (sd: Option[]) => {
    if (sd && sd.length > 0) {
        isLoadingOptions.value = true;
        try {
            const requestTypes = await supportStore.getServiceDeskRequestTypes(sd[0].value);
            filterOptions.requestTypeOptions = [];
            if (requestTypes !== undefined) {
                requestTypes.forEach((requestType) => {
                    filterOptions.requestTypeOptions.push({
                        value: requestType.name,
                        label: requestType.name,
                    });
                });
            }
            const requestTypeStatuses = await supportStore.getServiceDeskStatuses(sd[0].value);
            filterOptions.requestTypeStatusOptions = [];
            if (requestTypeStatuses !== undefined) {
                requestTypeStatuses.forEach((status) => {
                    filterOptions.requestTypeStatusOptions.push({
                        value: status.name,
                        label: status.name,
                    });
                });
            }
        } finally {
            isLoadingOptions.value = false;
        }
    } else {
        filterOptions.requestTypeOptions = [];
        filterOptions.requestTypeStatusOptions = [];
    }
};

watch(
    () => props.selectedFilter,
    async (newVal) => {
        // Deep copy to avoid reference issues
        // check if service desk changed to avoid refetch if not needed?
        // Original code watches selectedFilter.serviceDesks specifically for setupOptions
        // And watches selectedFilter deep for filterCopy sync.

        // We can do same
        const parsed = JSON.parse(JSON.stringify(newVal));
        // Check if service desk changed from current copy before overwriting?
        // Actually, existing logic just overwrites.
        filterCopy.value = parsed;

        // Setup options is called via the watch on filterCopy.value.serviceDesks?
        // Yes, if filterCopy.value.serviceDesks changes, that watcher fires.
    },
    { deep: true },
);
</script>
